import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '../../../components/ui/input';
import { Alert } from '../../../components/ui/alert';
import { Eye, EyeOff } from 'lucide-react';
import { LoadingButton } from '../../../components/common';
import { useChangePassword } from '../hooks/useSettings';

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, '現在のパスワードを入力してください'),
    newPassword: z
      .string()
      .min(8, 'パスワードは8文字以上で入力してください')
      .regex(/[a-z]/, '小文字を含めてください')
      .regex(/[A-Z]/, '大文字を含めてください')
      .regex(/[0-9]/, '数字を含めてください')
      .regex(/[^a-zA-Z0-9]/, '記号を含めてください'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'パスワードが一致しません',
    path: ['confirmPassword'],
  });

type PasswordFormData = z.infer<typeof passwordSchema>;

export const PasswordSettings: React.FC = () => {
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const { mutate: changePassword, isPending } = useChangePassword();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = (data: PasswordFormData) => {
    changePassword(
      { currentPassword: data.currentPassword, newPassword: data.newPassword },
      {
        onSuccess: () => {
          reset();
        },
      }
    );
  };

  return (
    <div className="border rounded-lg">
      <div className="p-6 pb-2">
        <h3 className="text-base font-semibold">パスワード変更</h3>
      </div>
      <div className="p-6 pt-2">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <label htmlFor="current-password" className="block text-sm font-medium mb-1">現在のパスワード</label>
            <Controller
              name="currentPassword"
              control={control}
              render={({ field }) => (
                <div className="relative">
                  <Input
                    {...field}
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="pr-10"
                    aria-describedby="current-password-error"
                    aria-invalid={!!errors.currentPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    aria-label={showCurrentPassword ? "パスワードを隠す" : "パスワードを表示"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              )}
            />
            {errors.currentPassword && (
              <p id="current-password-error" role="alert" className="text-sm text-red-500 mt-1">{errors.currentPassword.message}</p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="new-password" className="block text-sm font-medium mb-1">新しいパスワード</label>
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <div className="relative">
                  <Input
                    {...field}
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    className="pr-10"
                    aria-describedby="new-password-error"
                    aria-invalid={!!errors.newPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    aria-label={showNewPassword ? "パスワードを隠す" : "パスワードを表示"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              )}
            />
            {errors.newPassword && (
              <p id="new-password-error" role="alert" className="text-sm text-red-500 mt-1">{errors.newPassword.message}</p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="confirm-password" className="block text-sm font-medium mb-1">新しいパスワード（確認）</label>
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <div className="relative">
                  <Input
                    {...field}
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="pr-10"
                    aria-describedby="confirm-password-error"
                    aria-invalid={!!errors.confirmPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? "パスワードを隠す" : "パスワードを表示"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              )}
            />
            {errors.confirmPassword && (
              <p id="confirm-password-error" role="alert" className="text-sm text-red-500 mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Alert variant="info" className="mt-4">
            パスワードは8文字以上で、大文字・小文字・数字・記号を含める必要があります。
          </Alert>

          <div className="mt-6 flex justify-end">
            <LoadingButton type="submit" loading={isPending}>
              パスワードを変更
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
};
